import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Package, Plus, ExternalLink, Loader2, X } from "lucide-react";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import Dialog from "@/components/common/Dialog";
import { useNavigate } from "react-router-dom";
import useDeliverablesQuery, { DeliverableListItem } from "@/api/query/useDeliverablesQuery";
import { useQueryClient } from "@tanstack/react-query";
import ApiRequest from "@/api/ApiRequest";
import { requestURLs } from "@/Environment";
import { QUERY_KEYS } from "@/api/query/querykeys";
import { toast } from "react-toastify";
import { useLinkTasksMutation } from "@/api/mutation/useDeliverableMutations";
import Select, { StylesConfig } from "react-select";

interface OptionType {
  value: string;
  label: string;
  deliverable: DeliverableListItem;
}

const customSelectStyles: StylesConfig<OptionType, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "38px",
    height: "38px",
    borderRadius: "0.75rem",
    border: state.isFocused ? "1.5px solid #7C3AED" : "1px solid #E2E8F0",
    fontSize: "12px",
    fontWeight: "500",
    backgroundColor: "#FFFFFF",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(124, 58, 237, 0.15)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#7C3AED" : "#CBD5E1",
    },
    cursor: "pointer",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 10px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#94A3B8",
    fontSize: "12px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#1E293B",
    fontSize: "12px",
    fontWeight: "600",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.75rem",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #F1F5F9",
    overflow: "hidden",
    zIndex: 99999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "4px",
    maxHeight: "180px",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    fontWeight: "500",
    borderRadius: "0.5rem",
    margin: "1px 0",
    padding: "8px 10px",
    color: state.isSelected ? "#FFFFFF" : state.isFocused ? "#5B21B6" : "#334155",
    backgroundColor: state.isSelected
      ? "#7C3AED"
      : state.isFocused
      ? "#F5F3FF"
      : "transparent",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#6D28D9",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
};

interface LinkedDeliverableItem {
  deliverableTaskId?: string;
  deliverableId: string;
  deliverable: {
    deliverableId: string;
    deliverableNameId: string;
    name: string;
    status: string;
  };
}

interface TaskLinkedDeliverablesPopoverProps {
  taskId: string;
  projectId: string;
  deliverables?: LinkedDeliverableItem[];
}

const statusBadgeStyles: Record<string, string> = {
  NOT_STARTED: "bg-slate-100 text-slate-700 border-slate-200",
  IN_PROGRESS: "bg-blue-50 text-blue-700 border-blue-200",
  DELIVERED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REWORK_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
};

export const TaskLinkedDeliverablesPopover: React.FC<TaskLinkedDeliverablesPopoverProps> = ({
  taskId,
  projectId,
  deliverables = [],
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [selectedDeliverableId, setSelectedDeliverableId] = useState("");

  const count = deliverables.length;

  const openManageDialog = () => {
    setIsTooltipOpen(false);
    setIsManageDialogOpen(true);
  };

  // Query all project deliverables when manage dialog is opened
  const { data: allDeliverablesData, isLoading: isLoadingAll } = useDeliverablesQuery(
    isManageDialogOpen ? projectId : undefined,
    { limit: 100 }
  );

  const linkMutation = useLinkTasksMutation(selectedDeliverableId, projectId);

  const linkedDeliverableIds = new Set(deliverables.map((d) => d.deliverableId));
  const availableDeliverables: DeliverableListItem[] = (allDeliverablesData?.data?.items ?? []).filter(
    (d: DeliverableListItem) => !linkedDeliverableIds.has(d.deliverableId)
  );

  const handleLink = async () => {
    if (!selectedDeliverableId) return;
    try {
      await linkMutation.mutateAsync([taskId]);
      toast.success(t("deliverable_linked_successfully") || "Deliverable linked successfully");
      setSelectedDeliverableId("");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
    } catch {
      toast.error(t("failed_to_link_deliverable") || "Failed to link deliverable");
    }
  };

  const handleUnlink = async (deliverableId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await ApiRequest.delete(
        `${requestURLs.deliverables}/detail/${deliverableId}/tasks/${taskId}`
      );
      toast.success(t("deliverable_unlinked_successfully") || "Deliverable unlinked successfully");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.allTask, projectId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.deliverables, projectId] });
    } catch {
      toast.error(t("failed_to_unlink_deliverable") || "Failed to unlink deliverable");
    }
  };

  return (
    <>
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen} delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openManageDialog();
              }}
              className="p-0 border-0 bg-transparent inline-flex items-center gap-0.5 shrink-0 cursor-pointer text-violet-600 hover:text-violet-700"
            >
              <Package className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold leading-none">{count}</span>
            </button>
          </TooltipTrigger>

          <TooltipContent
            side="bottom"
            sideOffset={8}
            align="start"
            className="w-80 max-w-[340px] p-3.5 bg-white border border-slate-200/90 rounded-2xl shadow-xl select-none"
          >
            <div className="flex flex-col gap-2.5 font-normal">
              {/* Tooltip Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <div className="flex items-center gap-1.5 font-semibold text-xs text-slate-900">
                  <Package className="w-4 h-4 text-violet-600" />
                  <span className="text-sm font-bold text-slate-800">{t("linked_deliverables") || "Linked deliverables"}</span>
                </div>
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700">
                  {count}
                </span>
              </div>

              {/* Tooltip Items List (Top 3) */}
              <div className="flex flex-col gap-2">
                {deliverables.slice(0, 3).map((item) => {
                  const statusClass =
                    statusBadgeStyles[item.deliverable.status] ||
                    "bg-slate-100 text-slate-700 border-slate-200";
                  const formattedStatus = item.deliverable.status
                    .replace(/_/g, " ")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase());

                  return (
                    <div
                      key={item.deliverableId}
                      className="flex flex-col gap-1 p-2 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer border border-transparent hover:border-slate-100"
                      onClick={() => {
                        setIsTooltipOpen(false);
                        navigate(`/deliverables/detail/${item.deliverable.deliverableId}`);
                      }}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-bold text-violet-700 shrink-0">
                          {item.deliverable.deliverableNameId}
                        </span>
                        <span className="text-slate-300 text-xs">·</span>
                        <span className="text-xs text-slate-800 truncate font-medium">
                          {item.deliverable.name}
                        </span>
                      </div>
                      <div className="mt-0.5">
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-md border ${statusClass}`}>
                          {t(`deliverable_status_${item.deliverable.status.toLowerCase()}`) || formattedStatus}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tooltip Footer Actions: Link Deliverable (+) and Show more */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openManageDialog();
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 hover:underline cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>{t("link_deliverable") || "Link deliverable"}</span>
                </button>

                {count > 3 && (
                  <span
                    className="text-secondary text-xs font-medium underline cursor-pointer"
                    onClick={() => openManageDialog()}
                  >
                    {t("show_more") || "show more"}
                  </span>
                )}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Full Modal Dialog using common/Dialog */}
      <Dialog
        isOpen={isManageDialogOpen}
        onClose={() => setIsManageDialogOpen(false)}
        hasBackdropClose={true}
        hasEscClose={true}
        modalClass="max-w-md w-full p-0 overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-violet-600" />
            <span className="text-sm font-bold text-slate-900">
              {t("linked_deliverables") || "Linked deliverables"}
            </span>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700">
              {count}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setIsManageDialogOpen(false)}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 max-h-[360px] overflow-y-auto flex flex-col gap-2">
          {count === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">
              {t("no_linked_deliverables") || "No deliverables linked yet"}
            </p>
          ) : (
            deliverables.map((item) => {
              const statusClass =
                statusBadgeStyles[item.deliverable.status] ||
                "bg-slate-100 text-slate-700 border-slate-200";
              const formattedStatus = item.deliverable.status
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase());

              return (
                <div
                  key={item.deliverableId}
                  className="flex items-start justify-between gap-2 p-3 rounded-xl border border-slate-100 hover:border-slate-200 bg-white hover:bg-slate-50/70 transition-all group"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <div className="p-1.5 rounded-lg bg-violet-50 text-violet-600 shrink-0 mt-0.5">
                      <Package className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-violet-700 shrink-0">
                          {item.deliverable.deliverableNameId}
                        </span>
                        <span className="text-slate-300 text-xs">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            setIsManageDialogOpen(false);
                            navigate(`/deliverables/detail/${item.deliverable.deliverableId}`);
                          }}
                          className="text-xs font-semibold text-slate-800 hover:text-violet-600 hover:underline truncate text-left inline-flex items-center gap-1"
                          title={item.deliverable.name}
                        >
                          <span className="truncate">{item.deliverable.name}</span>
                          <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </div>
                      <div className="mt-1">
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-md border ${statusClass}`}>
                          {t(`deliverable_status_${item.deliverable.status.toLowerCase()}`) || formattedStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    title={t("unlink_deliverable") || "Unlink deliverable"}
                    onClick={(e) => handleUnlink(item.deliverable.deliverableId, e)}
                    className="p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all shrink-0 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Add / Link section */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/40">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-slate-700">
              {t("select_deliverable_to_link") || "Select deliverable to link"}
            </span>
            {isLoadingAll ? (
              <div className="py-2 flex items-center justify-center gap-2 text-xs text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t("loading") || "Loading..."}</span>
              </div>
            ) : availableDeliverables.length === 0 ? (
              <div className="py-2 text-xs text-slate-400 text-center">
                {t("no_more_deliverables_available") || "All deliverables already linked"}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select<OptionType, false>
                    value={
                      availableDeliverables
                        .map((d) => ({
                          value: d.deliverableId,
                          label: `${d.deliverableNameId} - ${d.name}`,
                          deliverable: d,
                        }))
                        .find((opt) => opt.value === selectedDeliverableId) || null
                    }
                    onChange={(option) => {
                      setSelectedDeliverableId(option ? option.value : "");
                    }}
                    options={availableDeliverables.map((d) => ({
                      value: d.deliverableId,
                      label: `${d.deliverableNameId} - ${d.name}`,
                      deliverable: d,
                    }))}
                    placeholder={t("select_deliverable") || "Select deliverable..."}
                    styles={customSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    isClearable={true}
                  />
                </div>
                <button
                  type="button"
                  disabled={!selectedDeliverableId || linkMutation.isPending}
                  onClick={handleLink}
                  className="h-[38px] px-3.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-50 transition-all cursor-pointer flex items-center gap-1.5 shrink-0 shadow-sm shadow-violet-600/20"
                >
                  {linkMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5" />
                      <span>{t("link") || "Link"}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};
